import React, { useMemo, useCallback } from "react";
import { Cascader } from "antd";

/**
 * Finds the path to a target value in a nested options tree
 */
const findValuePath = (options, targetValue, currentPath = []) =>
  options.reduce((result, option) => {
    if (result) return result;

    const newPath = [...currentPath, option.value];

    if (option.value === targetValue) {
      return newPath;
    }

    if (option.children?.length) {
      return findValuePath(option.children, targetValue, newPath);
    }

    return null;
  }, null);

/**
 * Cleans temporary properties from tree nodes
 */
const cleanTreeNode = node => {
  const cleaned = {
    label: node.label,
    value: node.value,
  };

  // Don't disable industry nodes - they should look enabled but not be selectable
  // We handle the non-selection logic in handleChange instead

  if (node.children?.length) {
    cleaned.children = node.children.map(cleanTreeNode);
  }

  return cleaned;
};

/**
 * Builds cascader options from flat opportunity types list
 * @param {Array} opportunityTypes - List of opportunity types from API
 * @param {boolean} onlyParentSelectable - If true, returns flat list of parent items only
 */
const buildOpportunityStatusTree = (opportunityTypes, onlyParentSelectable = false) => {
  if (!opportunityTypes?.length) return [];

  const industriesMap = new Map();
  const itemsMap = new Map();

  // First pass: Create nodes for all items + auto-create missing parents from parentType data
  opportunityTypes.forEach(item => {
    const industryId = String(item.industry?.id || "other");
    const industryName = item.industry?.name || "Other";

    if (!industriesMap.has(industryId)) {
      industriesMap.set(industryId, {
        label: industryName,
        value: `industry-${industryId}`,
        children: [],
      });
    }

    const itemId = String(item.id);
    if (!itemsMap.has(itemId)) {
      itemsMap.set(itemId, {
        label: item.name,
        value: item.id,
        children: [],
        parentTypeId: item.parentType?.id ? String(item.parentType.id) : null,
        industryId,
        isReal: true,
      });
    } else {
      // Item was auto-created as a missing parent earlier, update with real data
      const existing = itemsMap.get(itemId);
      existing.label = item.name;
      existing.value = item.id;
      existing.parentTypeId = item.parentType?.id ? String(item.parentType.id) : null;
      existing.industryId = industryId;
      existing.isReal = true;
    }

    // Auto-create missing parent node from embedded parentType data
    if (item.parentType?.id) {
      const parentId = String(item.parentType.id);
      if (!itemsMap.has(parentId)) {
        itemsMap.set(parentId, {
          label: item.parentType.name,
          value: item.parentType.id,
          children: [],
          parentTypeId: null,
          industryId,
          isReal: false, // Not in the list — display only, not selectable
        });
      }
    }
  });

  // Second pass: Build parent-child relationships
  itemsMap.forEach(currentItem => {
    if (currentItem.parentTypeId) {
      const parent = itemsMap.get(currentItem.parentTypeId);
      if (parent) {
        parent.children.push(currentItem);
      } else {
        // Shouldn't happen since we auto-create, but fallback to industry
        const industry = industriesMap.get(currentItem.industryId);
        industry?.children.push(currentItem);
      }
    } else {
      // Root item — add directly to industry
      const industry = industriesMap.get(currentItem.industryId);
      industry?.children.push(currentItem);
    }
  });

  // Clean up temporary properties for cascader
  const cleanNode = (node, depth = 0) => {
    const cleaned = { label: node.label, value: node.value };

    if (node.children && node.children.length > 0) {
      cleaned.children = node.children.map(child => cleanNode(child, depth + 1));
    }

    // When onlyParentSelectable is true, disable child-level nodes (depth 2 = children under parents)
    if (onlyParentSelectable && depth === 2) {
      cleaned.disabled = true;
    }

    return cleaned;
  };

  return Array.from(industriesMap.values()).map(node => cleanNode(node, 0));
};
/**
 * Search filter for cascader
 */
const searchFilter = (inputValue, path) =>
  path.some(option => option.label?.toLowerCase().includes(inputValue.toLowerCase()));

const SelectOpportunityStatus = ({
  opportunityTypes = [],
  value,
  onChange,
  placeholder = "Select opportunity type",
  className = "",
  loading = false,
  allowClear = true,
  showSearch = true,
  onlyParentSelectable = false,
  // Allow selecting any node (parent OR child), excluding industry headers.
  // Ignored when `onlyParentSelectable` is true.
  allowAnyLevelSelectable = false,
  ...rest
}) => {
  const cascaderOptions = useMemo(
    () => buildOpportunityStatusTree(opportunityTypes, onlyParentSelectable),
    [opportunityTypes, onlyParentSelectable]
  );

  const displayValue = useMemo(() => {
    if (value == null) return undefined;
    const path = findValuePath(cascaderOptions, value);
    return path?.length ? path : undefined;
  }, [value, cascaderOptions]);

  const handleChange = useCallback(
    selectedPath => {
      if (!selectedPath?.length) {
        onChange?.(undefined);
        return;
      }

      // Industry nodes are expandable but not selectable — ignore if user clicks one
      const leafValue = selectedPath[selectedPath.length - 1];
      if (String(leafValue).startsWith("industry-")) return;

      onChange?.(leafValue);
    },
    [onChange]
  );

  const handleParentSelectChange = useCallback(
    selectedPath => {
      if (!selectedPath?.length) {
        onChange?.(undefined);
        return;
      }

      const leafValue = selectedPath[selectedPath.length - 1];

      // Industry nodes are expandable but not selectable
      if (String(leafValue).startsWith("industry-")) return;

      // Only allow selection of parent-level nodes (depth 1 in the path = [industry, parent])
      if (selectedPath.length === 2) {
        onChange?.(leafValue);
      }
    },
    [onChange]
  );

  const searchConfig = useMemo(() => (showSearch ? { filter: searchFilter } : false), [showSearch]);

  return (
    <Cascader
      {...rest}
      {...(displayValue !== undefined && { value: displayValue })}
      showSearch={searchConfig}
      className={`w-full ${className}`}
      onChange={onlyParentSelectable ? handleParentSelectChange : handleChange}
      options={cascaderOptions}
      placeholder={placeholder}
      loading={loading}
      allowClear={allowClear}
      expandTrigger="hover"
      changeOnSelect={onlyParentSelectable || allowAnyLevelSelectable}
      displayRender={labels => {
        // Filter out industry labels since they're just containers
        const filteredLabels = labels.filter((label, index) => {
          // If this is the first item and it matches industry pattern, skip it
          const isIndustryLabel =
            index === 0 &&
            cascaderOptions.some(opt => opt.label === label && opt.value.startsWith("industry-"));
          return !isIndustryLabel;
        });
        return filteredLabels.join(" / ");
      }}
    />
  );
};

export default SelectOpportunityStatus;
